/**
 * UpgradeModal — global modal triggered by any 402 response from the backend.
 *
 * Mount once at the top of the app. The axios interceptor in @/lib/api fires
 * `markovlab:upgrade-required` on any 402; this component listens and shows
 * a friendly "Upgrade to Pro" dialog instead of the raw error.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Zap } from "lucide-react";

const FEATURE_COPY = {
  "wheel-safe-strikes": {
    title: "Wheel Screener strike engines",
    blurb: "Walk the live chain to surface alternative short strikes where the regime-adjusted P(non-touch) clears your threshold. Standard with Pro.",
  },
  "wheel-candidates": {
    title: "Wheel Candidates Scanner",
    blurb: "Ranks your watchlist by wheel suitability — IV rank, dividend cushion, earnings distance, and regime — all in one place. Standard with Pro.",
  },
  "portfolio/sorm": {
    title: "Portfolio SORM",
    blurb: "Stochastic Outcome Risk Modelling against your full book — Monte Carlo paths, floor breach probability, and tail risk. Standard with Pro.",
  },
  default: {
    title: "Pro Feature",
    blurb: "This is part of the MarkovLab Pro toolkit. Upgrade to unlock — cancel any time.",
  },
};


function detectFeature(url) {
  if (!url) return "default";
  if (url.includes("wheel-safe")) return "wheel-safe-strikes";
  if (url.includes("wheel-candidates")) return "wheel-candidates";
  if (url.includes("portfolio/sorm")) return "portfolio/sorm";
  return "default";
}

export default function UpgradeModal() {
  const [open, setOpen] = useState(false);
  const [feature, setFeature] = useState("default");
  const navigate = useNavigate();

  useEffect(() => {
    const onUpgrade = (e) => {
      const url = e?.detail?.url || "";
      if (url.includes("/broker/")) return;
      setFeature(detectFeature(url));
      setOpen(true);
    };
    window.addEventListener("markovlab:upgrade-required", onUpgrade);
    return () => window.removeEventListener("markovlab:upgrade-required", onUpgrade);
  }, []);

  const copy = FEATURE_COPY[feature] || FEATURE_COPY.default;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="upgrade-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 tracking-tight">
            <span className="inline-flex items-center justify-center w-7 h-7" style={{ background: "var(--hoip-profit)", color: "#000" }}>
              <Zap className="w-4 h-4" />
            </span>
            {copy.title}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm" style={{ color: "var(--hoip-text-secondary)" }} data-testid="upgrade-modal-blurb">{copy.blurb}</p>
        <ul className="text-xs space-y-1.5 mt-2" style={{ color: "var(--hoip-text-secondary)" }}>
          <li>• Wheel Screener strike engines + Wheel Candidates Scanner</li>
          <li>• Portfolio SORM (stochastic outcome modelling)</li>
          <li>• Advanced labs in development</li>
        </ul>
        <div className="flex items-center gap-3 mt-4">
          <button
            onClick={() => setOpen(false)}
            className="px-3 py-1.5 text-xs border"
            style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}
            data-testid="upgrade-modal-dismiss"
          >
            Not now
          </button>
          <button
            onClick={() => { setOpen(false); navigate("/billing"); }}
            className="ml-auto px-4 py-1.5 text-xs font-bold tracking-widest"
            style={{ background: "var(--hoip-text-primary)", color: "var(--hoip-bg)" }}
            data-testid="upgrade-modal-cta"
          >
            UPGRADE TO PRO →
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
